import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './OkfContextExportSettings.css';

// "AI Context Export Settings (OKF)" — drives the /api/okf endpoints (okf-lld §9).
// Lets an org enable the OKF Context Engine, pick a delivery mode (Git sync or a
// downloadable signed export), and generate/download point-in-time bundles.

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '—');

export default function OkfContextExportSettings() {
  const { t } = useTranslation();
  const DELIVERY_MODES = [
    { value: 'MANAGED_EXPORT', label: t('OkfContextExportSettings.deliveryModeManagedExport') },
    { value: 'GIT_SYNC', label: t('OkfContextExportSettings.deliveryModeGitSync') },
    { value: 'BOTH', label: t('OkfContextExportSettings.deliveryModeBoth') },
  ];
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    deliveryMode: 'MANAGED_EXPORT',
    scheduleCron: '0 0 * * * *',
    provider: 'GITHUB',
    repoUrl: '',
    branch: 'cataam-okf',
    signingEnabled: true,
    redactionProfile: '',
  });
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [toast, setToast] = useState(null);
  const [mcpKey, setMcpKey] = useState(null);
  const [keyGen, setKeyGen] = useState(false);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        axiosInstance.get('/okf/status'),
        axiosInstance.get('/okf/exports'),
      ]);
      setStatus(s.data || {});
      setForm((f) => ({
        ...f,
        enabled: !!s.data?.enabled,
        deliveryMode: s.data?.deliveryMode || f.deliveryMode,
      }));
      setExports(Array.isArray(e.data) ? e.data : []);
    } catch (err) {
      setToast({ type: 'error', message: t('OkfContextExportSettings.toastLoadFailed') });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const gitMode = form.deliveryMode === 'GIT_SYNC' || form.deliveryMode === 'BOTH';
  const exportMode = form.deliveryMode === 'MANAGED_EXPORT' || form.deliveryMode === 'BOTH';

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.put('/okf/config', {
        enabled: form.enabled,
        deliveryMode: form.deliveryMode,
        scheduleCron: form.scheduleCron,
        provider: gitMode ? form.provider : null,
        repoUrl: gitMode ? form.repoUrl : null,
        branch: form.branch,
        signingEnabled: form.signingEnabled,
        redactionProfile: form.redactionProfile || null,
      });
      const c = res.data || {};
      setForm((f) => ({
        ...f,
        enabled: !!c.enabled,
        deliveryMode: c.deliveryMode || f.deliveryMode,
        scheduleCron: c.scheduleCron || f.scheduleCron,
        provider: c.provider || f.provider,
        repoUrl: c.repoUrl || '',
        branch: c.branch || f.branch,
        signingEnabled: c.signingEnabled !== undefined ? c.signingEnabled : f.signingEnabled,
        redactionProfile: c.redactionProfile || '',
      }));
      setToast({ type: 'success', message: t('OkfContextExportSettings.toastSettingsSaved') });
      loadAll();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.message || t('OkfContextExportSettings.toastSaveFailed') });
    } finally {
      setSaving(false);
    }
  };

  const generateExport = async () => {
    setGenerating(true);
    try {
      await axiosInstance.post('/okf/export');
      setToast({ type: 'success', message: t('OkfContextExportSettings.toastExportGenerated') });
      loadAll();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.message || t('OkfContextExportSettings.toastExportFailed') });
    } finally {
      setGenerating(false);
    }
  };

  const syncGit = async () => {
    setSyncing(true);
    try {
      await axiosInstance.post('/okf/resync');
      setToast({ type: 'success', message: t('OkfContextExportSettings.toastGitSyncTriggered') });
      loadAll();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.message || t('OkfContextExportSettings.toastGitSyncFailed') });
    } finally {
      setSyncing(false);
    }
  };

  const copy = (text) => {
    try { navigator.clipboard.writeText(text); setToast({ type: 'success', message: t('OkfContextExportSettings.toastCopied') }); }
    catch { setToast({ type: 'error', message: t('OkfContextExportSettings.toastCopyFailed') }); }
  };

  const generateMcpKey = async () => {
    setKeyGen(true);
    try {
      const res = await axiosInstance.post('/integrations/api-keys', { name: 'OKF · Claude MCP' });
      setMcpKey(res.data?.rawKey || null);
      setToast({ type: 'success', message: t('OkfContextExportSettings.toastApiKeyGenerated') });
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.message || t('OkfContextExportSettings.toastApiKeyFailed') });
    } finally {
      setKeyGen(false);
    }
  };

  const pinExport = async (version) => {
    try {
      await axiosInstance.post(`/okf/exports/${encodeURIComponent(version)}/pin`);
      setToast({ type: 'success', message: t('OkfContextExportSettings.toastExportPinned') });
      loadAll();
    } catch (err) {
      setToast({ type: 'error', message: t('OkfContextExportSettings.toastPinFailed') });
    }
  };

  const download = async (version, artifact) => {
    const key = `${version}:${artifact}`;
    setDownloading(key);
    try {
      const res = await axiosInstance.get(`/okf/exports/${encodeURIComponent(version)}/download`, {
        params: { artifact },
        responseType: 'blob',
      });
      const names = { bundle: `${version}.zip`, log: 'log.md', manifest: 'MANIFEST.json', signature: 'bundle.sig' };
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = names[artifact] || 'okf-download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setToast({ type: 'error', message: t('OkfContextExportSettings.toastDownloadFailed') });
    } finally {
      setDownloading('');
    }
  };

  return (
    <div className="okf-page">
      {toast && (
        <div className={`okf-toast okf-toast-${toast.type}`}>
          <i className={`fas fa-${toast.type === 'success' ? 'check-circle' : 'exclamation-circle'}`} />
          {toast.message}
        </div>
      )}

      <div className="okf-intro">
        <h2><i className="fas fa-cloud-upload-alt" /> {t('OkfContextExportSettings.introHeading')}</h2>
        <p>{t('OkfContextExportSettings.introBody')}</p>
      </div>

      {loading ? (
        <div className="okf-empty"><i className="fas fa-spinner fa-spin" /> {t('OkfContextExportSettings.loading')}</div>
      ) : (
        <>
          {/* STATUS */}
          <div className="okf-card">
            <div className="okf-card-head">
              <h3>{t('OkfContextExportSettings.statusHeading')}</h3>
              <button className="okf-btn-ghost" onClick={loadAll} title={t('OkfContextExportSettings.refresh')}>
                <i className="fas fa-sync" /> {t('OkfContextExportSettings.refresh')}
              </button>
            </div>
            <div className="okf-status-grid">
              <div>
                <span className="okf-label">{t('OkfContextExportSettings.statusEngine')}</span>
                <span className={`okf-badge ${status?.enabled ? 'on' : 'off'}`}>
                  {status?.enabled ? t('OkfContextExportSettings.enabled') : t('OkfContextExportSettings.disabled')}
                </span>
              </div>
              <div><span className="okf-label">{t('OkfContextExportSettings.statusDeliveryMode')}</span><span className="okf-val">{status?.deliveryMode || '—'}</span></div>
              <div><span className="okf-label">{t('OkfContextExportSettings.statusLastGitCommit')}</span><span className="okf-val mono">{status?.lastCommitSha ? status.lastCommitSha.substring(0, 10) : '—'}</span></div>
              <div><span className="okf-label">{t('OkfContextExportSettings.statusLastPushed')}</span><span className="okf-val">{fmtDate(status?.lastPushedAt)}</span></div>
              <div><span className="okf-label">{t('OkfContextExportSettings.statusProcessedSeq')}</span><span className="okf-val">{status?.lastProcessedSeq ?? 0}</span></div>
              <div><span className="okf-label">{t('OkfContextExportSettings.statusExports')}</span><span className="okf-val">{status?.exportCount ?? 0}</span></div>
            </div>
          </div>

          {/* SETTINGS */}
          <div className="okf-card">
            <div className="okf-card-head"><h3>{t('OkfContextExportSettings.settingsHeading')}</h3></div>

            <label className="okf-switch-row">
              <input type="checkbox" checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              <span>{t('OkfContextExportSettings.enableEngine')}</span>
            </label>

            <div className="okf-field">
              <label>{t('OkfContextExportSettings.deliveryMode')}</label>
              <select value={form.deliveryMode}
                onChange={(e) => setForm({ ...form, deliveryMode: e.target.value })}>
                {DELIVERY_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>

            <div className="okf-field">
              <label>{t('OkfContextExportSettings.syncScheduleLabel')}</label>
              <input type="text" value={form.scheduleCron}
                onChange={(e) => setForm({ ...form, scheduleCron: e.target.value })} placeholder="0 0 * * * *" />
              <small>{t('OkfContextExportSettings.syncScheduleHelp')}</small>
            </div>

            {gitMode && (
              <div className="okf-subcard">
                <div className="okf-subcard-title"><i className="fas fa-code-branch" /> {t('OkfContextExportSettings.gitRepositoryTitle')}</div>
                <div className="okf-field">
                  <label>{t('OkfContextExportSettings.provider')}</label>
                  <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
                    <option value="GITHUB">GitHub</option>
                    <option value="GITLAB">GitLab</option>
                    <option value="BITBUCKET">Bitbucket</option>
                  </select>
                </div>
                <div className="okf-field">
                  <label>{t('OkfContextExportSettings.repositoryUrl')}</label>
                  <input type="url" value={form.repoUrl}
                    onChange={(e) => setForm({ ...form, repoUrl: e.target.value })}
                    placeholder="https://github.com/your-org/compliance-bundle.git" />
                </div>
                <div className="okf-field">
                  <label>{t('OkfContextExportSettings.branch')}</label>
                  <input type="text" value={form.branch}
                    onChange={(e) => setForm({ ...form, branch: e.target.value })} placeholder="cataam-okf" />
                  <small>{t('OkfContextExportSettings.branchHelp')}</small>
                </div>
                <p className="okf-note">
                  <i className="fas fa-key" /> {t('OkfContextExportSettings.gitCredentialNote')}
                </p>
              </div>
            )}

            <label className="okf-switch-row">
              <input type="checkbox" checked={form.signingEnabled}
                onChange={(e) => setForm({ ...form, signingEnabled: e.target.checked })} />
              <span>{t('OkfContextExportSettings.signExports')}</span>
            </label>

            <div className="okf-field">
              <label>{t('OkfContextExportSettings.redactionProfile')}</label>
              <select value={form.redactionProfile}
                onChange={(e) => setForm({ ...form, redactionProfile: e.target.value })}>
                <option value="">{t('OkfContextExportSettings.redactionNone')}</option>
                <option value="pii">{t('OkfContextExportSettings.redactionPii')}</option>
              </select>
            </div>

            <button className="okf-btn-primary" onClick={saveConfig} disabled={saving}>
              {saving ? <><i className="fas fa-spinner fa-spin" /> {t('OkfContextExportSettings.saving')}</> : <><i className="fas fa-save" /> {t('OkfContextExportSettings.saveSettings')}</>}
            </button>
          </div>

          {/* CONNECT AN AI AGENT (MCP) */}
          <div className="okf-card">
            <div className="okf-card-head"><h3>{t('OkfContextExportSettings.connectAgentHeading')}</h3></div>
            <p className="okf-note" style={{ marginTop: 0 }}>
              <i className="fas fa-robot" /> {t('OkfContextExportSettings.connectAgentNote')}
            </p>
            {!mcpKey ? (
              <button className="okf-btn-primary" onClick={generateMcpKey} disabled={keyGen}>
                {keyGen ? <><i className="fas fa-spinner fa-spin" /> {t('OkfContextExportSettings.generating')}</> : <><i className="fas fa-key" /> {t('OkfContextExportSettings.generateApiKey')}</>}
              </button>
            ) : (
              <div className="okf-keybox">
                <div className="okf-keywarn"><i className="fas fa-exclamation-triangle" /> {t('OkfContextExportSettings.keyOnceWarning')}</div>
                <div className="okf-coderow">
                  <code className="okf-code">{mcpKey}</code>
                  <button className="okf-btn-ghost" onClick={() => copy(mcpKey)}><i className="fas fa-copy" /> {t('OkfContextExportSettings.copy')}</button>
                </div>
                <p className="okf-label" style={{ marginTop: '0.9rem' }}>{t('OkfContextExportSettings.useWithMcpServer')}</p>
                <div className="okf-coderow">
                  <pre className="okf-code okf-snippet">{`CATAAM_API_KEY=${mcpKey}\nnpx -y cataam-mcp-server@0.1.5`}</pre>
                  <button className="okf-btn-ghost" onClick={() => copy(`CATAAM_API_KEY=${mcpKey}\nnpx -y cataam-mcp-server@0.1.5`)}><i className="fas fa-copy" /> {t('OkfContextExportSettings.copy')}</button>
                </div>
              </div>
            )}
          </div>

          {/* EXPORTS */}
          <div className="okf-card">
            <div className="okf-card-head">
              <h3>{t('OkfContextExportSettings.exportBundlesHeading')}</h3>
              <div className="okf-actions">
                {gitMode && (
                  <button className="okf-btn-ghost" onClick={syncGit} disabled={syncing}>
                    {syncing ? <><i className="fas fa-spinner fa-spin" /> {t('OkfContextExportSettings.syncing')}</> : <><i className="fas fa-code-branch" /> {t('OkfContextExportSettings.syncToGitNow')}</>}
                  </button>
                )}
                <button className="okf-btn-primary" onClick={generateExport} disabled={generating}>
                  {generating ? <><i className="fas fa-spinner fa-spin" /> {t('OkfContextExportSettings.generating')}</> : <><i className="fas fa-file-archive" /> {t('OkfContextExportSettings.generateExportNow')}</>}
                </button>
              </div>
            </div>

            {!exportMode && (
              <p className="okf-note"><i className="fas fa-info-circle" /> {t('OkfContextExportSettings.gitSyncOnlyNote')}</p>
            )}

            {exports.length === 0 ? (
              <div className="okf-empty"><i className="fas fa-box-open" /> {t('OkfContextExportSettings.noExports')}</div>
            ) : (
              <div className="okf-table">
                <div className="okf-tr okf-th">
                  <div>{t('OkfContextExportSettings.colVersion')}</div><div>{t('OkfContextExportSettings.colStatus')}</div><div>{t('OkfContextExportSettings.colFiles')}</div><div>{t('OkfContextExportSettings.colBundleSha')}</div><div>{t('OkfContextExportSettings.colGenerated')}</div><div>{t('OkfContextExportSettings.colDownload')}</div><div></div>
                </div>
                {exports.map((ex) => (
                  <div className="okf-tr" key={ex.version}>
                    <div className="mono">{ex.version}{ex.pinned && <i className="fas fa-thumbtack okf-pin" title={t('OkfContextExportSettings.pinned')} />}</div>
                    <div><span className={`okf-badge ${ex.status === 'READY' ? 'on' : ex.status === 'FAILED' ? 'fail' : 'pending'}`}>{ex.status}</span></div>
                    <div>{ex.fileCount}</div>
                    <div className="mono okf-sha" title={ex.bundleSha256}>{ex.bundleSha256 ? `${ex.bundleSha256.substring(0, 16)}…` : '—'}</div>
                    <div>{fmtDate(ex.generatedAt)}</div>
                    <div className="okf-dl">
                      <button className="okf-link" disabled={ex.status !== 'READY' || downloading === `${ex.version}:bundle`} onClick={() => download(ex.version, 'bundle')} title={t('OkfContextExportSettings.downloadBundleTitle')}>
                        <i className="fas fa-file-archive" /> {t('OkfContextExportSettings.bundle')}
                      </button>
                      <button className="okf-link" disabled={ex.status !== 'READY' || downloading === `${ex.version}:log`} onClick={() => download(ex.version, 'log')} title={t('OkfContextExportSettings.downloadLogTitle')}>
                        <i className="fas fa-stream" /> log.md
                      </button>
                      <button className="okf-link" disabled={ex.status !== 'READY' || downloading === `${ex.version}:manifest`} onClick={() => download(ex.version, 'manifest')} title={t('OkfContextExportSettings.downloadManifestTitle')}>
                        <i className="fas fa-list" /> {t('OkfContextExportSettings.manifest')}
                      </button>
                    </div>
                    <div>
                      {!ex.pinned && (
                        <button className="okf-icon" title={t('OkfContextExportSettings.pinTitle')} onClick={() => pinExport(ex.version)}>
                          <i className="fas fa-thumbtack" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
